export const UserFieldLengths = {
    FirstName: 30,
    LastName: 30,
    Email: 254,
    Username: 254,
    PhoneNumber: 10,
    Password: 100,
    Pin: 6,
    AssociatePin: 5,
};

export const KioskFieldLengths = {
    Name: 256,
    SerialNumber: 25,
    MachineID: 100,
};

export const DealerKioskFieldLengths = {
    Dispatcher: 2,
    VehiclePriority: 2,
    FtpImageFolder: 25,
    MobileSiteSubdomain: 50,
    Timeout: 3,
};

export const KioskBannerFieldLengths = {
    ImageUrl: 256,
};

export const SmsWhitelistFieldLengths = {
    Name: 254,
    Username: 254,
};

export const DealerGroupFieldLengths = {
    Name: 100,
};

export const DealerFieldLengths = {
    Name: 256,
    Street: 255,
    City: 255,
    ZipCode: 5,
    PostalCode: 7,
    PhoneNumber: 10,
    Website: 150,
    CifNumber: 7,
    KioskDisplayName: 256,
    Subdomain: 100,
    ReynoldsDealerId: 25,
    ReynoldsStoreId: 10,
    ReynoldsAreaId: 10,
    AutomateDealerId: 25,
    AutosoftDealerId: 10,
    CdkFortellisDealerId: 50,
    DealerbuiltDealerId: 50,
    DealertrackDealerId: 4,
    DealertrackEnterpriseCode: 4,
    DealertrackServerName: 25,
    DealertrackMasterId: 4,
    ActiveAdvisors: 20,
    PbsDealerId: 100,
    QuorumDealerId: 100,
    SertiDealerId: 100,
    HunterToken: 100,
    RecallDealerId: 50,
    RecallOpcode: 20,
    XtimeStoreCode: 100,
    XtimeStoreId: 100,
    DefaultCommentOpcode: 20,
    DefaultAddonOpcodes: 350,
    DefaultDispatchOpcode: 20,
    DefaultLaborType: 20,
    DefaultTaxCode: 4,
    DefaultDepartmentCode: 2,
    OnlinePaymentsEmail: 64,
    PaymentAccountLegalBusinessName: 30,
    PaymentAccountDBABusinessName: 50,
    PaymentAccountMCCCode: 4,
    PaymentAccountFirstName: 15,
    PaymentAccountLastName: 25,
    PaymentAccountComments: 5000,
    PaymentAccountNumber: 10,
    PaymentAccountMerchantId: 50,
    PaymentAccountTerminalId: 40,
    PaymentAccountToken: 140,
    TextSubaccountSid: 34,
    TextAuthorizationToken: 32,
    TextMessagingServiceSid: 34,
    TagNumberOpcode: 50,
    // TEMP
    TextContactOpCode: 34,
    TextingSubdomain: 15,
    KeyTrakSystemId: 6,
};

export const CommunicationsFieldLengths = {
    SenderAlias: 100,
    ReplyTo: 254,
    TextContactOpCode: 34,
    CustomMessage: 2000,
    Signature: 2000,
    TestReminderEmail: 254,
} as const;

export const AdvisorFieldLengths = {
    FirstName: 50,
    LastName: 50,
    AdvisorNumber: 25,
};

export const FortellisSwapTablesFieldLengths = {
    Name: 1024,
    OpCode: 50,
    Description: 1024,
    LaborType: 20,
    DispatchCode: 20,
    CdkSubscriptionId: 50,
};

/**
 * The maximum length of all fields in the HunterVehicle component and sub-components.
 */
export const HunterVehicleFieldLengths: Readonly<Record<string, number>> = {
    /**
     * VINs can have a maximum of 17 characters after 1980.
     */
    Vin: 17,
    Year: 4,
    Make: 256,
    Model: 256,
    VehicleSpec: 256,
    VehicleSpeed: 2,
    StoppingDistance: 3,
    IdealStoppingDistance: 3,
    TireReading: 10,
} as const;

export const GeneralSettingsFieldLengths = {
    FacilityId: 15,
} as const;

/**
 * Field length constants for the Disclaimers and Custom Messages settings page.
 * Based on column sizes in the `KeydropAdmin_dealer` and `KeydropAdmin_transportation_option` tables.
 */
export const DisclaimersAndCustomMessagesFieldLengths = {
    /**
     * Max length for general disclaimer/instruction text fields (nvarchar(2048))
     */
    Generic: 2048,
    /**
     * Max length for transportation option closing messages (nvarchar(300))
     */
    ClosingMessage: 300,
    /**
     * Max length for transportation option DMS opcode fields (nvarchar(20))
     */
    TransportationOpcode: 20,
    /**
     * Max length for the pickup time instructions opcode field (nvarchar(20))
     */
    PickupTimeOpcode: 20,
} as const;

export const ValuationsFieldLengths = {
    BlackbookAdjustment: 10,
    BlackbookMinimumValuation: 10,
    CrmLeadNameOrEmail: 256,
} as const;

export const CustomQuestionFieldLengths = {
    CustomUpsellName: 100,
    CustomUpsellOpCode: 25,
} as const;

/**
 * Constant object containing the exact field lengths for each column that is edited on the Edit Service screen.
 *
 * The comment above each property on this object
 */
export const ServiceFieldLengths = {
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `name`
     * Column Type: `varchar(255)`
     */
    Name: 255,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `spanish_name`
     * Column Type: `varchar(255)`
     */
    SpanishName: 255,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `img_path`
     * Column Type: `varchar(1024)`
     */
    ImagePath: 1024,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `img_url`
     * Column Type: `varchar(1024)`
     */
    ImageUrl: 1024,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `spanish_img_url`
     * Column Type: `varchar(1024)`
     */
    SpanishImageUrl: 1024,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `price_details`
     * Column Type: `varchar(5120)`
     */
    PriceDetails: 5120,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `spanish_price_details`
     * Column Type: `varchar(5120)`
     */
    SpanishPriceDetails: 5120,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `item_reference_id`
     * Column Type: `varchar(100)`
     */
    ItemReferenceId: 100,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `line_payment_method`
     * Column Type: `varchar(1)`
     */
    LinePaymentMethod: 1,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `service_type`
     * Column Type: `varchar(2)`
     *
     * The column must stay at varchar(2) because the legacy Django site is backed
     * by the same table. `serviceType` is persisted as the integer SupportServiceType
     * enum value cast to a string ("0"–"3") to fit within that width.
     */
    ServiceType: 2,
    /**
     * This character limit is not based on a limitation with the database.
     */
    Description: 400,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `generic_disclaimer_red`
     * Column Type: `varchar(512)`
     */
    GenericDisclaimerRed: 512,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `generic_price_disclaimer`
     * Column Type: `varchar(512)`
     */
    GenericPriceDisclaimer: 512,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `generic_price_title_1`
     * Column Type: `varchar(64)`
     */
    GenericPriceTitle1: 64,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `generic_price_title_2`
     * Column Type: `varchar(64)`
     */
    GenericPriceTitle2: 64,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `spanish_generic_disclaimer_red`
     * Column Type: `varchar(512)`
     */
    SpanishGenericDisclaimerRed: 512,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `spanish_generic_price_disclaimer`
     * Column Type: `varchar(512)`
     */
    SpanishGenericPriceDisclaimer: 512,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `spanish_generic_price_title_1`
     * Column Type: `varchar(64)`
     */
    SpanishGenericPriceTitle1: 64,
    /**
     * Table: `KeydropAdmin_service`
     * Column Name: `spanish_generic_price_title_2`
     * Column Type: `varchar(64)`
     */
    SpanishGenericPriceTitle2: 64,
} as const;